// React and external dependencies
import { useSet } from '@mantine/hooks';

import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as randomUUID } from 'uuid';

// Internal dependencies
import { materialTypeFormat } from '@/common/material-type-formatting';
import { useGetUploadUrlMutation } from '@/common/utils/graphql.generated';
import { useFacility } from '@/contexts/facility-context/hook';
import { useCurrentUser } from '@/hooks';

// Generated types and GraphQL hooks
import { cleanCode } from '@/utils';
import {
	MaterialType,
	MediaType,
	ScanEventPhotoType,
} from '../../../../../../backend/src/types.generated';
import {
	useFacilityBaleOutboundMutation,
	useSearchTrackableAssetsForFacilitiesBaleOutboundLazyQuery,
	useUploadOutboundBalePhotoMutation,
} from './graphql.generated';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Represents the different pages/steps in the bale outbound workflow
 */
export type BaleOutboundPageType =
	| 'ROOT'
	| 'ADD_SCAN'
	| 'REMOVE_SCAN'
	| 'CONFIRM_MATERIAL'
	| 'ADD_DOCKET_PHOTO'
	| 'ADD_LOAD_PHOTOS'
	| 'CONFIRM_SUBMISSION'
	| 'SUCCESS';

/**
 * Status types for bale outbound operations
 */
export type BaleOutboundStatus =
	| 'PENDING'
	| 'VERIFYING'
	| 'CONFIRMED'
	| 'DUPLICATE'
	| 'ERROR'
	| 'INVALID'
	| null;

type ScanEventFromFacilityOutboundError =
	| 'NOT_AUTHENTICATED'
	| 'NOT_AUTHORISED'
	| 'NOT_AUTHORISED_FOR_FACILITY'
	| 'FACILITY_NOT_AUTHORISED_FOR_BALE_OUTBOUND'
	| 'INVALID_CODE'
	| 'NO_BALES_FOUND'
	| 'BALE_ALREADY_DISPATCHED'
	| 'CODE_NOT_FOUND'
	| 'NETWORK_ERROR'
	| 'CREATION_FAILED'
	| 'PHOTO_UPLOAD_FAILED'
	| 'OVER_WEIGHT_LIMIT';

/**
 * Represents a scanned bale with its material type and associated codes
 */
interface ScannedBale {
	name: string;
	value: MaterialType;
	emoji: string;
	count: number;
	codes: string[];
}

/**
 * Form data structure for the bale outbound workflow
 */
interface BaleOutboundFormData {
	docketId: string | null;
	docketPhoto: string | null;
	weight: number | null;
	truckRegistration: string | null;
	photos: string[];
}

/**
 * Main context interface defining all available state and actions
 */
export interface BaleOutboundContextValue {
	// Navigation state
	page: BaleOutboundPageType;
	setPage: React.Dispatch<React.SetStateAction<BaleOutboundPageType>>;

	// Loading and error states
	loadingScan: boolean;
	error: ScanEventFromFacilityOutboundError | null;
	setError: React.Dispatch<React.SetStateAction<ScanEventFromFacilityOutboundError | null>>;

	// Form data and handlers
	formData: BaleOutboundFormData;
	handleInput: (key: keyof BaleOutboundFormData, value: string | number | string[] | null) => void;
	handlePhotosInput: (photos: string[]) => void;

	// Bale scanning functionality
	scannedBales: ScannedBale[];
	handleBaleScan: (action: 'ADD' | 'REMOVE', code: string) => Promise<void>;

	// Submission
	canSubmit: boolean;
	handleCreateOutboundBale: () => Promise<
		| ({
				__typename?: 'ScanEventPhoto';
				id: string;
		  } | null)[]
		| undefined
	>;

	// Workflow management
	resetWorkflow: () => void;

	// Submission
	loadingSubmission: boolean;
}

/**
 * Props for the BaleOutboundContextProvider component
 */
interface BaleOutboundContextProviderProps {
	children: ReactNode;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

/**
 * Context for managing bale outbound workflow state and operations
 */
// eslint-disable-next-line react-refresh/only-export-components
export const BaleOutboundContext = createContext<BaleOutboundContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

/**
 * Provider component for the bale outbound workflow context.
 *
 * This component manages all state related to the bale outbound process including:
 * - Navigation between workflow steps
 * - Form data collection (docket, photos, customer info, etc.)
 * - Bale scanning and validation
 * - Photo upload and submission
 *
 * @param children - React children to wrap with the context
 */
export const BaleOutboundContextProvider: React.FC<BaleOutboundContextProviderProps> = ({
	children,
}) => {
	// ========================================================================
	// HOOKS AND EXTERNAL STATE
	// ========================================================================

	const { currentFacility } = useFacility();
	const { currentUser } = useCurrentUser();

	// GraphQL mutations and queries
	const [searchTrackableAssets] = useSearchTrackableAssetsForFacilitiesBaleOutboundLazyQuery();
	const [uploadOutboundBalePhotos] = useUploadOutboundBalePhotoMutation();
	const [getUploadUrl] = useGetUploadUrlMutation();
	const [facilityBaleOutbound] = useFacilityBaleOutboundMutation();

	// ========================================================================
	// LOCAL STATE
	// ========================================================================

	// Navigation and UI state
	const [page, setPage] = useState<BaleOutboundPageType>('ROOT');
	const [loadingScan, setLoadingScan] = useState(false);
	const [error, setError] = useState<ScanEventFromFacilityOutboundError | null>(null);
	const [loadingSubmission, setLoadingSubmission] = useState(false);

	// Code tracking sets for preventing duplicates and tracking invalid codes
	const currentCodes = useSet();
	const invalidCodes = useSet();

	// Form data state
	const [formData, setFormData] = useState<BaleOutboundFormData>({
		docketId: null,
		docketPhoto: null,
		weight: null,
		truckRegistration: null,
		photos: [],
	});

	// Handles weight limit warning
	useEffect(() => {
		if (formData.weight && formData.weight > 50000) {
			setError('OVER_WEIGHT_LIMIT');
		}
	}, [formData.weight]);

	// Initialize scanned bales with all material types at zero count
	const [scannedBales, setScannedBales] = useState<ScannedBale[]>(
		Object.values(MaterialType)
			.map((materialType) => {
				const config = materialTypeFormat[materialType];
				return {
					name: config.label,
					value: materialType,
					emoji: config.emoji,
					count: 0,
					codes: [],
				};
			})
			.reverse()
	);

	// ========================================================================
	// FORM INPUT HANDLERS
	// ========================================================================

	/**
	 * Generic form input handler for updating form data
	 */
	const handleInput = (
		key: keyof BaleOutboundFormData,
		value: string | number | string[] | null
	) => {
		setFormData((prev) => ({ ...prev, [key]: value }));
	};

	/**
	 * Specialized handler for photo array updates
	 */
	const handlePhotosInput = (photos: string[]) => {
		setFormData((prev) => ({ ...prev, photos }));
	};

	// ========================================================================
	// BALE SCANNING LOGIC
	// ========================================================================

	/**
	 * Handles scanning bale codes (both adding and removing)
	 *
	 * This function:
	 * 1. Validates the scanned code
	 * 2. Checks for duplicates or invalid codes
	 * 3. Queries the API to get bale information
	 * 4. Updates the scanned bales state accordingly
	 *
	 * @param action - Whether to ADD or REMOVE the scanned code
	 * @param code - The scanned barcode/QR code
	 */
	const handleBaleScan = async (action: 'ADD' | 'REMOVE', code: string) => {
		try {
			const cleaned = cleanCode(code);
			// Check for previously invalidated codes
			if (invalidCodes.has(cleaned)) {
				setError('INVALID_CODE');
				return;
			}

			// Handle ADD action validation
			if (action === 'ADD') {
				// Prevent duplicate scans in current session
				if (currentCodes.has(cleaned)) {
					return;
				}
			}

			// Handle REMOVE action validation
			if (action === 'REMOVE') {
				// Check if code actually exists in scanned bales before API call
				const codeExists = scannedBales.some((bale) => bale.codes.includes(cleaned));
				if (!codeExists) {
					return;
				}
			}

			setLoadingScan(true);

			// Query API to validate and get bale information
			const data = await searchTrackableAssets({
				variables: { filter: { scannableCode: cleaned } },
			}).then((res) => {
				if (res.data?.trackableAssets?.[0]) {
					return res.data.trackableAssets[0];
				}
				return null;
			});

			// Validate API response
			if (!data) {
				setError('INVALID_CODE');
				return;
			}

			// Check bale status - only allow BALE_CREATED status
			if (data?.status && data.status !== 'BALE_CREATED') {
				setError('INVALID_CODE');
				return;
			}

			const materialType = data.intendedMaterial;

			// Update scanned bales state
			setScannedBales((prev) => {
				const newBales = [...prev];
				const index = newBales.findIndex((bale) => bale.value === materialType);

				if (index !== -1) {
					if (action === 'ADD') {
						// Add code if it doesn't already exist
						if (!newBales[index].codes.includes(cleaned)) {
							newBales[index].codes.push(cleaned);
							newBales[index].count += 1;
							currentCodes.add(cleaned); // Track in current session
						}
					} else if (action === 'REMOVE') {
						// Remove code if it exists
						const codeIndex = newBales[index].codes.indexOf(cleaned);
						if (codeIndex !== -1) {
							newBales[index].codes.splice(codeIndex, 1);
							newBales[index].count = Math.max(0, newBales[index].count - 1);
							currentCodes.delete(cleaned); // Remove from current session tracking
						}
					}
				}
				return newBales;
			});
		} catch {
			// Handle any network errors or API errors
			setError('NETWORK_ERROR');
		} finally {
			setLoadingScan(false);
		}
	};

	// ========================================================================
	// SUBMISSION LOGIC
	// ========================================================================

	/**
	 * Computed property to determine if the form can be submitted
	 * Requires all mandatory fields and at least one scanned bale
	 */
	const canSubmit = useMemo(() => {
		return Boolean(
			formData.docketId &&
				formData.weight &&
				formData.truckRegistration &&
				formData.docketPhoto &&
				formData.photos.length > 0 &&
				scannedBales.some((bale) => bale.count > 0)
		);
	}, [formData, scannedBales]);

	/**
	 * Handles the complete outbound bale creation and photo upload process
	 *
	 * This function:
	 * 1. Creates the outbound bale record via GraphQL mutation
	 * 2. Uploads all photos (load photos and docket photo) to S3
	 * 3. Associates uploaded photos with the scan event
	 *
	 * @returns Promise resolving to the created scan event photos or undefined on error
	 */
	const handleCreateOutboundBale = useCallback(async () => {
		// Validate required dependencies
		if (
			!currentFacility ||
			!currentUser ||
			!formData.docketId ||
			!formData.truckRegistration ||
			!formData.weight
		)
			return;

		try {
			// Set loading state
			setLoadingSubmission(true);

			// Step 1: Create the outbound bale record
			const { data, errors } = await facilityBaleOutbound({
				variables: {
					input: {
						facilityId: currentFacility.id,
						docketId: formData.docketId,
						truckRegistration: formData.truckRegistration,
						weight: formData.weight,
						codes: scannedBales.flatMap((bale) => bale.codes),
					},
				},
			});

			// Handle GraphQL errors
			if (errors && errors.length > 0) {
				const errorMessage = errors[0].message;
				// Check if it's one of our known error types
				const knownErrors: ScanEventFromFacilityOutboundError[] = [
					'NOT_AUTHENTICATED',
					'NOT_AUTHORISED',
					'NOT_AUTHORISED_FOR_FACILITY',
					'FACILITY_NOT_AUTHORISED_FOR_BALE_OUTBOUND',
					'INVALID_CODE',
					'NO_BALES_FOUND',
					'BALE_ALREADY_DISPATCHED',
					'CODE_NOT_FOUND',
					'NETWORK_ERROR',
					'CREATION_FAILED',
					'PHOTO_UPLOAD_FAILED',
				];

				if (knownErrors.includes(errorMessage as ScanEventFromFacilityOutboundError)) {
					setError(errorMessage as ScanEventFromFacilityOutboundError);
				} else {
					setError('NETWORK_ERROR');
				}
				return;
			}

			if (!data?.facilityBaleOutbound) {
				setError('CREATION_FAILED');
				return;
			}

			const scanEventId = data.facilityBaleOutbound.id;

			// Step 2: First upload all photos to S3
			const photoUploads: { filename: string; type: ScanEventPhotoType }[] = [];

			/**
			 * Helper function to upload a single photo to S3
			 */
			async function uploadPhotoToS3(photo: string, type: ScanEventPhotoType) {
				// Generate unique S3 key
				const s3Key = `trackable-assets/load/${randomUUID()}.jpg`;

				// Get signed upload URL
				const { data: uploadUrlData } = await getUploadUrl({
					variables: { key: s3Key },
				});

				if (!uploadUrlData?.getUploadUrl) {
					throw new Error('Failed to get upload URL');
				}

				// Convert photo to bytes and upload
				const photoBytes = await fetch(photo).then((res) => res.arrayBuffer());
				const response = await fetch(uploadUrlData.getUploadUrl.url, {
					method: 'PUT',
					body: photoBytes,
					headers: {
						'Content-Type': 'image/jpeg',
					},
				});

				if (!response.ok) {
					throw new Error('Failed to upload photo');
				}

				return {
					filename: uploadUrlData.getUploadUrl.filename,
					type: type,
				};
			}

			// Upload all photos to S3 in parallel
			const uploadPromises = [
				// Upload all load photos
				...formData.photos.map((photo) => uploadPhotoToS3(photo, 'LOAD' as ScanEventPhotoType)),
				// Upload docket photo if present
				...(formData.docketPhoto
					? [uploadPhotoToS3(formData.docketPhoto, 'DOCKET' as ScanEventPhotoType)]
					: []),
			];

			const uploadResults = await Promise.allSettled(uploadPromises);

			// Check for failed uploads
			const failedUploads = uploadResults.filter((result) => result.status === 'rejected');
			if (failedUploads.length > 0) {
				console.error('Some photos failed to upload to S3:', failedUploads);
				throw new Error('Failed to upload photos to S3');
			}

			// Collect successful uploads
			uploadResults.forEach((result) => {
				if (result.status === 'fulfilled') {
					photoUploads.push(result.value);
				}
			});

			// Step 3: Create all database records
			const createdPhotos: { id: string }[] = [];

			/**
			 * Helper function to create a database record for an uploaded photo
			 */
			async function createPhotoRecord(filename: string, type: ScanEventPhotoType) {
				const { data: photoData } = await uploadOutboundBalePhotos({
					variables: {
						input: {
							scanEvent: {
								id: scanEventId,
							},
							photo: {
								filename,
								type: MediaType.Image,
							},
							createdAt: new Date().toISOString(),
							createdByApplicationType: 'mrf',
							createdByUser: {
								id: currentUser?.id ?? '',
							},
							photoType: type,
						},
					},
				});

				if (!photoData?.createScanEventPhoto) {
					throw new Error('Failed to create photo record');
				}

				return photoData.createScanEventPhoto;
			}

			// Create all database records in parallel
			const dbPromises = photoUploads.map(({ filename, type }) =>
				createPhotoRecord(filename, type)
			);

			const dbResults = await Promise.allSettled(dbPromises);

			// Check for failed database creations
			const failedDbCreations = dbResults.filter((result) => result.status === 'rejected');
			if (failedDbCreations.length > 0) {
				console.error('Some photo records failed to create:', failedDbCreations);
				// Note: Photos are already uploaded to S3, so this is a partial failure
			}

			// Collect successful database records
			dbResults.forEach((result) => {
				if (result.status === 'fulfilled') {
					createdPhotos.push(result.value);
				}
			});

			// Navigate to success page on completion
			setPage('SUCCESS');

			return createdPhotos;
		} catch {
			// Handle any network errors or unexpected errors
			setError('PHOTO_UPLOAD_FAILED');
		} finally {
			setLoadingSubmission(false);
		}
	}, [
		formData,
		scannedBales,
		currentFacility,
		currentUser,
		uploadOutboundBalePhotos,
		getUploadUrl,
		facilityBaleOutbound,
	]);

	/**
	 * Resets the workflow state to the initial root page, clearing all form data, scanned bales, and errors.
	 */
	const resetWorkflow = useCallback(() => {
		setPage('ROOT');
		setFormData({
			docketId: null,
			docketPhoto: null,
			weight: null,
			truckRegistration: null,
			photos: [],
		});
		setScannedBales(
			Object.values(MaterialType)
				.map((materialType) => {
					const config = materialTypeFormat[materialType];
					return {
						name: config.label,
						value: materialType,
						emoji: config.emoji,
						count: 0,
						codes: [],
					};
				})
				.reverse()
		);
		setError(null);
	}, []);

	// ========================================================================
	// CONTEXT PROVIDER RETURN
	// ========================================================================

	return (
		<BaleOutboundContext.Provider
			value={{
				// Navigation
				page,
				setPage,

				// Loading and error states
				loadingScan,
				error,
				setError,

				// Form data and handlers
				formData,
				handleInput,
				handlePhotosInput,

				// Bale scanning
				scannedBales,
				handleBaleScan,

				// Submission
				canSubmit,
				handleCreateOutboundBale,
				loadingSubmission,
				resetWorkflow,
			}}
		>
			{children}
		</BaleOutboundContext.Provider>
	);
};
